"use client";
import React from "react";
import { cn } from "@/utils/urlUtils";

// Omit 'size' from InputHTMLAttributes since we're redefining it
interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  size?: "sm" | "md" | "lg";
  label?: string;
  labelPosition?: "left" | "right";
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      checked,
      onCheckedChange,
      size = "md",
      label,
      labelPosition = "right",
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = event.target.checked;
      onCheckedChange?.(newChecked);
    };

    const sizeClasses = {
      sm: {
        switch: "w-8 h-4",
        thumb: "h-3 w-3",
        translate: "translate-x-4"
      },
      md: {
        switch: "w-11 h-6",
        thumb: "h-5 w-5",
        translate: "translate-x-5"
      },
      lg: {
        switch: "w-14 h-7",
        thumb: "h-6 w-6",
        translate: "translate-x-7"
      }
    };

    return (
      <div className="flex items-center gap-2">
        {label && labelPosition === "left" && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            {label}
          </label>
        )}
        
        <div className="relative inline-flex items-center">
          <input
            type="checkbox"
            ref={ref}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only peer"
            {...props}
          />
          
          <div
            className={cn(
              "flex items-center rounded-full cursor-pointer transition-all duration-200 ease-in-out",
              sizeClasses[size].switch,
              checked 
                ? "bg-brand-500 dark:bg-brand-600" 
                : "bg-gray-300 dark:bg-gray-700",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => {
              if (!disabled && onCheckedChange) {
                onCheckedChange(!checked);
              }
            }}
          >
            <div
              className={cn(
                "rounded-full bg-white shadow-theme-xs transform transition-transform duration-200 ease-in-out",
                sizeClasses[size].thumb,
                checked ? sizeClasses[size].translate : "translate-x-0.5"
              )}
            />
          </div>
        </div>

        {label && labelPosition === "right" && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
            {label}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = "Switch";

export default Switch;